import { useNavigate } from 'react-router-dom'
import { Star, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLanguage } from '@/hooks/useLanguage'
import type { Lang } from '@/types/lang'
import { courseDisplayName, hasCourseName } from '@/lib/localizedText'
import { formatCompactNumber, formatRating } from '@/lib/courseMeta'
import { WorkshopMenu } from './WorkshopMenu'
import { CourseLangsBadge } from './CourseLangsBadge'
import { CourseStatusChip, type CourseCardStatus } from './CourseStatusChip'
import { CourseCoverImage } from './CourseCoverImage'

export interface CourseCardOverlayProps {
  id: number
  name: string | null
  nameEn: string | null
  banner: string | null
  rating?: number
  /** Пока не показываем — см. комментарий у строки с цифрами. */
  updatedAt?: string | null
  completedCount?: number
  variant?: 'catalog' | 'favorites' | 'workshop'
  onDelete?: () => void
  className?: string
  sourceLang?: Lang
  publishedLangs?: Lang[]
  /** Готовый, но не объявленный язык — карточка показывает его приглушённым
   *  бейджем. Считает владелец данных (`DashboardCourseGrid`), у которого на
   *  руках весь курс: здесь пропсы плоские. */
  unpublishedFilledLang?: Lang
  /** Готовая ссылка на курс в веб-академии (по `slug`), открывается в новой вкладке.
   *  Без неё карточка каталога некликабельна: номер в адрес не подставляем. */
  externalHref?: string
  /** Статус курса — чип на обложке (только «Мои курсы»). */
  status?: CourseCardStatus
}

export const CourseCardOverlay = ({
  id,
  name,
  nameEn,
  banner,
  rating,
  completedCount = 0,
  variant = 'catalog',
  status,
  onDelete,
  className,
  sourceLang,
  publishedLangs,
  unpublishedFilledLang,
  externalHref,
}: CourseCardOverlayProps) => {
  const navigate = useNavigate()
  // Язык ПОКАЗА — язык интерфейса. editor.contentLang отвечает только за то,
  // какой слот правится в редакторе, и с засевом из sourceLang утащил бы
  // названия чужих курсов в другой язык.
  const { language: lang } = useLanguage()
  const displayName = courseDisplayName({ name, nameEn }, lang)
  // Мастерская — редактор курса; иначе курс открывается в веб-академии в новой
  // вкладке: своей страницы курса у студии нет. Ссылку строит вызывающий из `slug`.
  const go = () => {
    if (externalHref) window.open(externalHref, '_blank', 'noopener')
    else if (variant === 'workshop') navigate(`/editor/${id}`)
  }

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === 'Enter') go()
      }}
      className={cn(
        // Карточка как в вебе академии: серая плитка, обложка 16:9 внутри, название и
        // строка с цифрами под ней. Описание не показываем — в академии его нет. Шрифт
        // академии (TT Commons, font-commons) задан и на корне, и на заголовке: глобальное правило
        // для h1..h6 иначе перебило бы наследование. Карточка — контейнер (`cqw`): текст
        // растёт с её шириной, пропорции везде как на ПК (~300px), кегли — `clamp`.
        'group relative flex w-full [container-type:inline-size] cursor-pointer flex-col rounded-[20px] font-commons bg-foreground/5 p-1 pb-4 outline-none transition duration-200 hover:scale-[1.02] hover:bg-foreground/[0.08] focus-visible:ring-2 focus-visible:ring-ring motion-reduce:hover:scale-100',
        className,
      )}
    >
      <div className="relative aspect-video w-full shrink-0 overflow-hidden rounded-[14px] bg-foreground/10">
        <CourseCoverImage seed={id} banner={banner} alt={displayName} />

        {status ? (
          <div className="absolute left-2 top-2 z-10">
            <CourseStatusChip status={status} />
          </div>
        ) : null}
        {variant === 'workshop' ? (
          <div className="absolute right-2 top-2 z-10">
            <WorkshopMenu id={id} onDelete={onDelete} />
          </div>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col px-3">
        <h3
          className={cn(
            'line-clamp-2 min-h-[calc(2.4em+0.75rem)] pt-3 font-commons text-[clamp(1.125rem,6cqw,1.5rem)] font-normal leading-[1.2]',
            // Без названия — серым: это подпись, а не название курса.
            hasCourseName({ name, nameEn }) ? null : 'text-muted-foreground',
          )}
        >
          {displayName}
        </h3>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 pt-2.5 text-[clamp(0.875rem,4.6cqw,1.125rem)] leading-none">
          {rating !== undefined ? (
            <span className="flex items-center gap-1 text-muted-foreground">
              <Star className="size-[1.07em] shrink-0" />
              {formatRating(rating)}
            </span>
          ) : null}
          <span className="flex items-center gap-1 text-muted-foreground">
            <Users className="size-[1.21em] shrink-0" />
            {formatCompactNumber(completedCount)}
          </span>
          {/* Дату изменения пока не показываем: сервер перезаписывает `updatedAt` у всех
              курсов разом, и на каждой карточке выходило «сегодня» (docs/petya-updated-at.md). */}
          {variant === 'workshop' ? (
            <CourseLangsBadge
              sourceLang={sourceLang}
              publishedLangs={publishedLangs}
              unpublishedFilledLang={unpublishedFilledLang}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
